import React, { useState, useEffect, useCallback, useRef } from 'react';
// import { useNavigate } from 'react-router-dom'; // Reserved for future navigation
import Header from '../components/header/Header';
import { GoalWizard } from '../components/GoalWizard';
import useStudyPlanner from '../hooks/useStudyPlanner';
import { GOAL_TYPE_INFO, TOPIC_STATUS, RESOURCE_TYPES } from '../services/StudyPlannerService';
import './StudyDashboard.css';

/**
 * StudyDashboard Page
 * 
 * Main dashboard for the Study Planner module.
 * Shows goal progress, roadmap, topics, and resources.
 */

const StudyDashboard = () => {
  // const navigate = useNavigate(); // Reserved for future navigation
  const candidateProfileId = localStorage.getItem('candidateProfileId');
  const candidateEmail = localStorage.getItem('candidateEmail');
  
  // Study Planner Hook
  const {
    isConnected,
    connect,
    hasGoal,
    goalType,
    goalData,
    hasRoadmap,
    roadmap,
    isGeneratingRoadmap,
    roadmapProgress,
    roadmapError,
    generateRoadmap,
    topicProgress,
    overallProgress,
    markTopicComplete,
    markTopicInProgress,
    practiceQuestions,
    isLoadingQuestions,
    getPracticeQuestions,
    resources,
    isLoadingResources,
    searchResources,
    isLoading,
    error: _error, // eslint-disable-line no-unused-vars
    startSession,
    resetStudyPlanner,
    updateGoal: _updateGoal, // eslint-disable-line no-unused-vars
    GOAL_TYPES: _GOAL_TYPES // eslint-disable-line no-unused-vars
  } = useStudyPlanner(candidateProfileId);

  // UI State
  const [showGoalWizard, setShowGoalWizard] = useState(false);
  const [selectedTopic, setSelectedTopic] = useState(null);
  // const [activeTab, setActiveTab] = useState('roadmap'); // Reserved for future tab navigation
  const [showPracticeModal, setShowPracticeModal] = useState(false);
  const [showResourcesModal, setShowResourcesModal] = useState(false);
  const [practiceSkill, setPracticeSkill] = useState('');
  const [resourceTopic, setResourceTopic] = useState('');
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  
  // Active section state - only one can be open at a time
  const [activeSection, setActiveSection] = useState(null);
  
  // Section refs for spotlight positioning
  const sectionRefs = {
    progress: useRef(null),
    skills: useRef(null),
    topics: useRef(null),
    schedule: useRef(null)
  };
  
  // Bulb position state - default to top-right
  const [bulbPosition, setBulbPosition] = useState({ top: 70, right: 30 });

  // Toggle section - only one active at a time with spotlight
  const toggleSection = (section) => {
    if (activeSection === section) {
      setActiveSection(null);
      setBulbPosition({ top: 70, right: 30 });
    } else {
      setActiveSection(section);
      // Update bulb position to hover over the active section (center of viewport for maximized)
      setTimeout(() => {
        setBulbPosition({
          top: 80,
          left: window.innerWidth / 2
        });
      }, 50);
    }
  };

  // Get candidate profile for wizard
  const [candidateProfile, setCandidateProfile] = useState(null);
  
  useEffect(() => {
    // Build candidate profile from localStorage
    setCandidateProfile({
      id: candidateProfileId,
      email: candidateEmail,
      fullName: localStorage.getItem('candidateFullName') || '',
      keySkills: localStorage.getItem('candidateKeySkills') || '',
      roleApplyFor: localStorage.getItem('candidateRoleApplyFor') || '',
      workStatus: localStorage.getItem('candidateWorkStatus') || '',
      educationDetails: localStorage.getItem('candidateEducation') || '',
      description: localStorage.getItem('candidateDescription') || ''
    });
  }, [candidateProfileId, candidateEmail]);

  // Auto-connect to WebSocket when we have a goal
  useEffect(() => {
    if (hasGoal && !isConnected) {
      connect();
    }
  }, [hasGoal, isConnected, connect]);

  // Handle goal wizard completion
  const handleGoalComplete = useCallback(async (goal) => {
    setShowGoalWizard(false);
    
    if (!candidateProfile) return;
    
    try {
      // Start session with the new goal
      await startSession(candidateProfile, goal);
      
      // Generate roadmap
      generateRoadmap({
        studyHoursPerDay: goal.studyHoursPerDay,
        focusOnPractice: goal.focusOnPractice
      });
    } catch (error) {
      console.error('Failed to start study session:', error);
    }
  }, [candidateProfile, startSession, generateRoadmap]);

  // Handle topic click
  const handleTopicClick = (topic) => {
    setSelectedTopic(selectedTopic?.topic_id === topic.topic_id ? null : topic);
  };

  // Handle mark complete
  const handleMarkComplete = (topicId) => {
    markTopicComplete(topicId, 0, '');
  };

  // Handle start topic
  const handleStartTopic = (topicId) => {
    markTopicInProgress(topicId);
  };

  // Handle practice questions
  const handleGetPractice = (skill) => {
    setPracticeSkill(skill);
    setShowPracticeModal(true);
    getPracticeQuestions(skill, 5);
  };

  // Handle resource search - Reserved for future use
  // const handleSearchResources = (topic) => {
  //   setResourceTopic(topic);
  //   setShowResourcesModal(true);
  //   searchResources(topic, RESOURCE_TYPES.ALL);
  // };

  // Handle reset
  const handleReset = async () => {
    await resetStudyPlanner();
    setShowResetConfirm(false);
    setShowGoalWizard(true);
  };

  // Get topic status display
  const getTopicStatus = (topicId) => {
    return topicProgress[topicId]?.status || TOPIC_STATUS.NOT_STARTED;
  };

  // Calculate weekly progress
  const getWeeklyProgress = () => {
    if (!roadmap?.weekly_schedule) return [];
    
    return roadmap.weekly_schedule.map((week, index) => {
      const weekTopics = roadmap.study_topics.filter(t => 
        week.focus_areas?.includes(t.skill_category)
      );
      const completed = weekTopics.filter(t => 
        getTopicStatus(t.topic_id) === TOPIC_STATUS.COMPLETED
      ).length;
      
      return {
        ...week,
        completed,
        total: weekTopics.length,
        percentage: weekTopics.length > 0 ? Math.round((completed / weekTopics.length) * 100) : 0
      };
    });
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="study-dashboard-page">
        <Header />
        <div className="loading-container">
          <div className="loading-spinner" />
          <p>Loading your study plan...</p>
        </div>
      </div>
    );
  }

  // No goal set - show GoalWizard directly
  if (!hasGoal) {
    return (
      <div className="study-dashboard-page">
        <Header />
        <GoalWizard
          isOpen={true}
          onClose={() => window.history.back()}
          onComplete={handleGoalComplete}
          candidateProfile={candidateProfile}
          isFullPage={true}
        />
      </div>
    );
  }

  return (
    <div className={`study-dashboard-page ${activeSection ? 'spotlight-active' : ''}`}>
      <Header />
      
      {/* Spotlight Overlay - click to close */}
      <div 
        className={`spotlight-overlay ${activeSection ? 'visible' : ''}`}
        onClick={() => {
          if (activeSection) {
            setActiveSection(null);
            setBulbPosition({ top: 70, right: 30 });
          }
        }}
      ></div>
      
      {/* Dynamic Hanging Light Bulb */}
      <div 
        className="light-bulb-container dynamic"
        style={{
          top: `${bulbPosition.top}px`,
          ...(bulbPosition.right !== undefined 
            ? { right: `${bulbPosition.right}px`, left: 'auto', transform: 'none' }
            : { left: `${bulbPosition.left}px`, right: 'auto', transform: 'translateX(-50%)' }
          )
        }}
      >
        <div className="light-cord"></div>
        <div className={`light-bulb ${activeSection ? 'on' : 'off'}`}>
          <div className="bulb-glow"></div>
          <div className="bulb-glass">
            <div className="bulb-filament"></div>
          </div>
          <div className="bulb-base"></div>
        </div>
        {activeSection && (
          <div className="spotlight-beam"></div>
        )}
      </div>

      {/* City Silhouette Background */}
      <div className="city-background">
        <div className="city-buildings"></div>
        <div className="city-buildings-colored"></div>
      </div>
      
      <div className="study-dashboard-container">
        {/* Header Thought Bubble */}
        <div className="thought-bubble header-bubble">
          <div className="bubble-content">
            <div className="header-left">
              <div className="goal-badge" style={{ backgroundColor: GOAL_TYPE_INFO[goalType]?.color }}>
                {GOAL_TYPE_INFO[goalType]?.label}
              </div>
              <h1>{goalData?.targetRole || 'Your Learning Path'}</h1>
              <p className="goal-timeline">
                {goalData?.timelineWeeks} weeks | {goalData?.studyHoursPerDay} hrs/day
              </p>
            </div>
            <div className="header-actions">
              <button className="btn-edit-goal" onClick={() => setShowGoalWizard(true)}>
                Edit Goal
              </button>
              <button className="btn-reset" onClick={() => setShowResetConfirm(true)}>
                Reset
              </button>
            </div>
          </div>
          <div className="bubble-tail"></div>
        </div>

        {/* Main Content Grid */}
        <div className="dashboard-grid">
          {/* Progress Section */}
          <div 
            ref={sectionRefs.progress}
            className={`spotlight-section progress-section ${activeSection === 'progress' ? 'active maximized' : activeSection ? 'dimmed' : ''}`}
          >
            <div className="section-header" onClick={() => toggleSection('progress')}>
              <span className="section-title">Progress</span>
              <span className="section-toggle">{activeSection === 'progress' ? 'X' : '+'}</span>
            </div>
            
            {activeSection === 'progress' && (
              <div className="section-content">
                <div className="progress-display">
                  <div className="progress-circle-large">
                    <span className="progress-value">{overallProgress.percentage}%</span>
                  </div>
                  <div className="progress-details">
                    <div className="stat-row">
                      <span className="stat-label">Topics</span>
                      <span className="stat-value">{roadmap?.study_topics?.length || 0}</span>
                    </div>
                    <div className="stat-row">
                      <span className="stat-label">Hours</span>
                      <span className="stat-value">{roadmap?.study_topics?.reduce((sum, t) => sum + (t.estimated_hours || 0), 0) || 0}h</span>
                    </div>
                    <div className="stat-row">
                      <span className="stat-label">Skills</span>
                      <span className="stat-value">{goalData?.targetSkills?.length || 0}</span>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Skills Section */}
          {roadmap?.skill_gaps && roadmap.skill_gaps.length > 0 && (
            <div 
              ref={sectionRefs.skills}
              className={`spotlight-section skills-section ${activeSection === 'skills' ? 'active maximized' : activeSection ? 'dimmed' : ''}`}
            >
              <div className="section-header" onClick={() => toggleSection('skills')}>
                <span className="section-title">Skills</span>
                <span className="section-badge">{roadmap.skill_gaps.length}</span>
                <span className="section-toggle">{activeSection === 'skills' ? 'X' : '+'}</span>
              </div>
              
              {activeSection === 'skills' && (
                <div className="section-content">
                  {roadmap.skill_gaps.map((gap, index) => (
                    <div key={index} className="skill-item">
                      <div className="skill-row">
                        <span className="skill-name">{gap.skill}</span>
                        <span className={`priority-indicator ${gap.gap_severity}`}>
                          {gap.gap_severity === 'high' ? 'H' : gap.gap_severity === 'medium' ? 'M' : 'L'}
                        </span>
                      </div>
                      <div className="skill-levels">
                        <span className="level-from">{gap.current_level}</span>
                        <span className="level-arrow">--&gt;</span>
                        <span className="level-to">{gap.target_level}</span>
                        <button className="practice-btn" onClick={() => handleGetPractice(gap.skill)}>Practice</button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Topics Section */}
          <div 
            ref={sectionRefs.topics}
            className={`spotlight-section topics-section ${activeSection === 'topics' ? 'active maximized' : activeSection ? 'dimmed' : ''}`}
          >
            <div className="section-header" onClick={() => toggleSection('topics')}>
              <span className="section-title">Topics</span>
              <span className="section-badge">{roadmap?.study_topics?.length || 0}</span>
              <span className="section-toggle">{activeSection === 'topics' ? 'X' : '+'}</span>
            </div>
            
            {activeSection === 'topics' && (
              <div className="section-content">
                <div className="topics-list">
                  {roadmap?.study_topics?.map((topic, index) => {
                    const status = getTopicStatus(topic.topic_id);
                    return (
                      <div 
                        key={topic.topic_id}
                        className={`topic-item ${status}`}
                        onClick={() => handleTopicClick(topic)}
                      >
                        <div className="topic-marker">
                          {status === TOPIC_STATUS.COMPLETED ? 'V' : status === TOPIC_STATUS.IN_PROGRESS ? '>' : index + 1}
                        </div>
                        <div className="topic-info">
                          <span className="topic-name">{topic.name}</span>
                          <span className="topic-time">{topic.estimated_hours}h</span>
                        </div>
                        <div className="topic-actions">
                          {status === TOPIC_STATUS.NOT_STARTED && (
                            <button onClick={(e) => { e.stopPropagation(); handleStartTopic(topic.topic_id); }}>Start</button>
                          )}
                          {status === TOPIC_STATUS.IN_PROGRESS && (
                            <button onClick={(e) => { e.stopPropagation(); handleMarkComplete(topic.topic_id); }}>Done</button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Schedule Section */}
          <div 
            ref={sectionRefs.schedule}
            className={`spotlight-section schedule-section ${activeSection === 'schedule' ? 'active maximized' : activeSection ? 'dimmed' : ''}`}
          >
            <div className="section-header" onClick={() => toggleSection('schedule')}>
              <span className="section-title">Schedule</span>
              <span className="section-toggle">{activeSection === 'schedule' ? 'X' : '+'}</span>
            </div>
            
            {activeSection === 'schedule' && (
              <div className="section-content">
                {getWeeklyProgress().map((week, index) => (
                  <div key={index} className="week-item">
                    <div className="week-header">
                      <span className="week-label">Week {week.week}</span>
                      <span className="week-percent">{week.percentage}%</span>
                    </div>
                    <div className="week-progress-bar">
                      <div 
                        className="week-progress-fill"
                        style={{ width: `${week.percentage}%` }}
                      ></div>
                    </div>
                    <div className="week-focus">
                      {week.focus_areas?.map((area, i) => (
                        <span key={i} className="focus-tag">{area}</span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Generating Roadmap State */}
        {isGeneratingRoadmap && (
          <div className="generating-overlay">
            <div className="generating-content">
              <div className="brain-animation">
                <div className="brain-pulse"></div>
                <div className="brain-icon">*</div>
              </div>
              <h3>Creating Your Personalized Roadmap</h3>
              <p>{roadmapProgress || 'Analyzing your skills and generating learning path...'}</p>
              <div className="thinking-dots">
                <span></span><span></span><span></span>
              </div>
            </div>
          </div>
        )}

        {/* Roadmap Error */}
        {roadmapError && (
          <div className="error-message">
            <p>{roadmapError}</p>
            <button onClick={() => generateRoadmap()}>Try Again</button>
          </div>
        )}

        {/* No Roadmap Yet */}
        {!hasRoadmap && !isGeneratingRoadmap && (
          <div className="generate-prompt">
            <div className="prompt-content">
              <div className="prompt-indicator">*</div>
              <h3>Ready to Generate Your Roadmap?</h3>
              <p>Based on your goal, we will create a personalized learning path</p>
              <button onClick={() => generateRoadmap()}>Generate Roadmap</button>
            </div>
          </div>
        )}
      </div>

      {/* Practice Questions Modal */}
      {showPracticeModal && (
        <div className="modal-overlay" onClick={() => setShowPracticeModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Practice: {practiceSkill}</h3>
              <button onClick={() => setShowPracticeModal(false)}>X</button>
            </div>
            <div className="modal-body">
              {isLoadingQuestions ? (
                <div className="loading-spinner small" />
              ) : practiceQuestions ? (
                <ul className="practice-questions">
                  {practiceQuestions.questions.map((q, idx) => (
                    <li key={idx}>{q}</li>
                  ))}
                </ul>
              ) : (
                <p>No questions available</p>
              )}
            </div>
            <div className="modal-footer">
              <button onClick={() => getPracticeQuestions(practiceSkill, 5)}>
                Get New Questions
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Resources Modal */}
      {showResourcesModal && (
        <div className="modal-overlay" onClick={() => setShowResourcesModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Resources: {resourceTopic}</h3>
              <button onClick={() => setShowResourcesModal(false)}>X</button>
            </div>
            <div className="modal-body">
              {isLoadingResources ? (
                <div className="loading-spinner small" />
              ) : resources ? (
                <>
                  {resources.summary && (
                    <p className="resources-summary">{resources.summary}</p>
                  )}
                  <div className="resources-list">
                    {resources.items?.map((r, idx) => (
                      <a 
                        key={idx}
                        href={r.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="resource-card"
                      >
                        <div className="resource-type">{r.type}</div>
                        <h4>{r.title}</h4>
                        {r.description && <p>{r.description}</p>}
                        <span className="external-link">Open</span>
                      </a>
                    ))}
                  </div>
                </>
              ) : (
                <p>No resources found</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Reset Confirmation Modal */}
      {showResetConfirm && (
        <div className="modal-overlay" onClick={() => setShowResetConfirm(false)}>
          <div className="modal-content small" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Reset Study Plan?</h3>
              <button onClick={() => setShowResetConfirm(false)}>X</button>
            </div>
            <div className="modal-body">
              <p>This will delete your current goal and all progress. This action cannot be undone.</p>
            </div>
            <div className="modal-footer">
              <button className="btn-cancel" onClick={() => setShowResetConfirm(false)}>
                Cancel
              </button>
              <button className="btn-danger" onClick={handleReset}>
                Reset
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Goal Wizard */}
      <GoalWizard
        isOpen={showGoalWizard}
        onClose={() => setShowGoalWizard(false)}
        onComplete={handleGoalComplete}
        candidateProfile={candidateProfile}
        initialGoal={goalData}
      />
    </div>
  );
};

export default StudyDashboard;
